import {getTestProps} from '@benchling/js-test-utils';
import {
  Anchor,
  Box,
  createStyles,
  Group,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import staticURL from 'lib/staticURL';
import {AgentFoundrySummary} from 'llm/chat-library/foundries/useAgentFoundries';
import {getChatWorkflowCategories, type ChatUseCase} from 'llm/chat-library/use-cases/chatUseCaseCatalog';
import type {FoundryUseCaseRunSource} from 'llm/chat-library/use-cases/recordChatUseCaseRun';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';

const PAGE_SIZE = 10;

const useStyles = createStyles((theme) => ({
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    minWidth: 800,
  },
  tableHeader: {
    backgroundColor: theme.colors.gray[0],
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },
  nameButton: {
    color: theme.colors.blue[7],
    fontWeight: 600,
    textAlign: 'left',
  },
  nameColumn: {
    width: '20%',
  },
  descriptionColumn: {
    width: '35%',
  },
  searchInput: {
    maxWidth: 480,
    width: '100%',
  },
}));

type PaginationControl = 'first' | 'previous' | 'next' | 'last';

const PAGINATION_CONTROL_LABELS: Record<PaginationControl, string> = {
  first: 'First page',
  previous: 'Previous page',
  next: 'Next page',
  last: 'Last page',
};

function getPaginationControlProps(control: PaginationControl): {'aria-label': string} {
  return {'aria-label': PAGINATION_CONTROL_LABELS[control]};
}

function getPaginationItemProps(page: number): {'aria-label': string} {
  return {'aria-label': `Page ${page}`};
}

const useEmptyStateStyles = createStyles((theme) => ({
  emptyState: {
    alignContent: 'center',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: `${theme.spacing.xl} 0`,
  },
  useCaseGrid: {
    display: 'grid',
    gap: theme.spacing.md,
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    maxWidth: 720,
    width: '100%',
  },
  useCase: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.lg,
    minHeight: 120,
    textAlign: 'left',
    width: '100%',
    '&:hover': {
      backgroundColor: theme.colors.blue[0],
      borderColor: theme.colors.blue[3],
    },
  },
  useCaseIcon: {
    alignItems: 'center',
    backgroundColor: theme.colors.blue[0],
    borderRadius: theme.radius.sm,
    color: theme.colors.blue[7],
    display: 'flex',
    flex: '0 0 24px',
    fontSize: theme.fontSizes.md,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  illustration: {
    height: 100,
    maxWidth: '100%',
    objectFit: 'contain',
    userSelect: 'none',
  },
}));

function AgentFoundryRow({
  agentFoundry,
  nameButtonClassName,
  onSelect,
}: {
  agentFoundry: AgentFoundrySummary;
  nameButtonClassName: string;
  onSelect: (agentFoundryId: string) => void;
}): JSX.Element {
  const handleSelect = (): void => {
    onSelect(agentFoundry.id);
  };

  return (
    <tr aria-label={agentFoundry.name}>
      <td>
        <Anchor
          component="button"
          type="button"
          size="sm"
          className={nameButtonClassName}
          aria-label={`Open ${agentFoundry.name}`}
          onClick={handleSelect}
        >
          {agentFoundry.name}
        </Anchor>
      </td>
      <td>
        <Tooltip
          events={{focus: true, hover: true, touch: false}}
          label={agentFoundry.description}
          multiline={true}
          openDelay={300}
          width={320}
          withinPortal={true}
        >
          <Text size="sm" lineClamp={3} tabIndex={0}>
            {agentFoundry.description}
          </Text>
        </Tooltip>
      </td>
      <td>
        <Text size="sm">{agentFoundry.creatorName ?? '—'}</Text>
      </td>
      <td>
        <Text size="sm">{readableTime(agentFoundry.modifiedAt, {showTime: false})}</Text>
      </td>
      <td>
        <Text size="sm">{agentFoundry.agents.length + (agentFoundry.coordinatorAgent == null ? 0 : 1)}</Text>
      </td>
    </tr>
  );
}

function AgentFoundriesEmptyState({
  onStartFoundryChat,
}: {
  onStartFoundryChat: (chatUseCase: ChatUseCase, source: FoundryUseCaseRunSource) => void;
}): JSX.Element {
  const {classes} = useEmptyStateStyles();
  const foundryCategory = getChatWorkflowCategories().find(({id}) => id === 'create-foundry');
  if (foundryCategory == null) {
    return <></>;
  }

  return (
    <Stack align="center" className={classes.emptyState} justify="safe center" spacing="xl">
      <Stack align="center" spacing="xs">
        <img
          alt=""
          aria-hidden="true"
          className={classes.illustration}
          src={staticURL('/static/img/agent-foundries/foundry-empty-state.png')}
          {...getTestProps({component: 'AgentFoundriesList', element: 'emptyStateIllustration'})}
        />
        <Title order={2}>Set up your first foundry</Title>
      </Stack>
      <Box className={classes.useCaseGrid}>
        {foundryCategory.chatUseCases.map((chatUseCase) => (
          <UnstyledButton
            key={chatUseCase.id}
            className={classes.useCase}
            onClick={() => {
              onStartFoundryChat(chatUseCase, 'foundry_page_card');
            }}
          >
            <Stack spacing="xs">
              <Group noWrap={true} spacing="sm">
                <Box className={classes.useCaseIcon}>
                  <BenchlingIcon aria-hidden={true} icon={chatUseCase.icon} />
                </Box>
                <Text fw="bold">{chatUseCase.label}</Text>
              </Group>
              <Text c="dimmed" size="sm">
                {chatUseCase.description}
              </Text>
            </Stack>
          </UnstyledButton>
        ))}
      </Box>
    </Stack>
  );
}

export default function AgentFoundriesList({
  agentFoundries,
  onSelect,
  onStartFoundryChat,
}: {
  agentFoundries: Array<AgentFoundrySummary>;
  onSelect: (agentFoundryId: string) => void;
  onStartFoundryChat: (chatUseCase: ChatUseCase, source: FoundryUseCaseRunSource) => void;
}): JSX.Element {
  const {classes} = useStyles();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const filteredFoundries = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (normalizedSearch === '') {
      return agentFoundries;
    }
    return agentFoundries.filter((agentFoundry) =>
      [agentFoundry.name, agentFoundry.description, agentFoundry.creatorName]
        .filter((value): value is string => value != null)
        .some((value) => value.toLowerCase().includes(normalizedSearch))
    );
  }, [agentFoundries, search]);

  const totalPages = Math.max(1, Math.ceil(filteredFoundries.length / PAGE_SIZE));
  const activePage = Math.min(page, totalPages);
  const rangeStart = (activePage - 1) * PAGE_SIZE;
  const visibleFoundries = filteredFoundries.slice(rangeStart, rangeStart + PAGE_SIZE);
  const rangeEnd = Math.min(rangeStart + PAGE_SIZE, filteredFoundries.length);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.currentTarget.value);
    setPage(1);
  };

  if (agentFoundries.length === 0) {
    return <AgentFoundriesEmptyState onStartFoundryChat={onStartFoundryChat} />;
  }

  return (
    <Stack spacing="md" {...getTestProps({component: 'AgentFoundriesList', element: 'root'})}>
      <TextInput
        aria-label="Search foundries"
        className={classes.searchInput}
        icon={<BenchlingIcon icon="search" />}
        onChange={handleSearchChange}
        placeholder="Search foundries"
        value={search}
      />

      {filteredFoundries.length === 0 ? (
        <Text fw="normal" c="dimmed" size="sm">
          No foundries match your search.
        </Text>
      ) : (
        <>
          <Paper withBorder={true} radius="md" className={classes.tableContainer}>
            <Table
              aria-label="Foundries"
              className={classes.table}
              horizontalSpacing="md"
              verticalSpacing="sm"
              highlightOnHover={true}
            >
              <thead className={classes.tableHeader}>
                <tr>
                  <th className={classes.nameColumn}>Name</th>
                  <th className={classes.descriptionColumn}>Description</th>
                  <th>Created by</th>
                  <th>Last Updated</th>
                  <th>Agents</th>
                </tr>
              </thead>
              <tbody>
                {visibleFoundries.map((agentFoundry) => (
                  <AgentFoundryRow
                    key={agentFoundry.id}
                    agentFoundry={agentFoundry}
                    nameButtonClassName={classes.nameButton}
                    onSelect={onSelect}
                  />
                ))}
              </tbody>
            </Table>
          </Paper>
          <Group position="apart" px="sm">
            <Text c="dimmed" size="sm">
              Showing {rangeStart + 1}–{rangeEnd} of {filteredFoundries.length}
            </Text>
            <Pagination
              getControlProps={getPaginationControlProps}
              getItemProps={getPaginationItemProps}
              onChange={setPage}
              size="sm"
              total={totalPages}
              value={activePage}
            />
          </Group>
        </>
      )}
    </Stack>
  );
}
